<script setup>
import { ref, computed, nextTick } from 'vue'
import { useWindowScroll } from '@vueuse/core'
import AutocompleteBar from './AutocompleteBar.vue'
import BannerAlert from './BannerAlert.vue'
import TopBar from './navigation/TopBar.vue'
import DesktopMenu from './navigation/DesktopMenu.vue'
import MobileMenu from './navigation/MobileMenu.vue'
import ImpersonateBar from './navigation/ImpersonateBar.vue'
import { useReachability } from './navigation/useReachability.js'

const props = defineProps({
    contact: { type: Object, required: true },
    header: { type: Object, required: true },
    openingHours: { type: Object, required: true },
    bannerAlert: { type: Object, default: null },
})

// When the `banner-alert` DataItem is active it replaces the contact bar with an alert
const showBanner = computed(() => Boolean(props.bannerAlert?.active && props.bannerAlert?.message))

const reachability = useReachability(props.openingHours)

// The main bar gets a little tighter once you scroll
const { y } = useWindowScroll()
const scrolled = computed(() => y.value > 8)

// On mobile the search bar is folded away behind a button
const showSearch = ref(false)
const search = ref(null)

function toggleSearch () {
    showSearch.value = !showSearch.value

    if (showSearch.value) {
        nextTick(() => search.value?.focus())
    }
}
</script>

<template>
    <header id="navigation" class="z-25 bg-white border-b border-gray-100">

        <BannerAlert v-if="showBanner" :banner="bannerAlert" />
        <TopBar v-else :contact="contact" :reachability="reachability" />

        <div class="bg-red-500 transition-[padding] duration-300" :class="scrolled ? 'py-3' : 'py-4'">
            <div class="container max-w-7xl flex flex-wrap md:flex-nowrap items-center gap-x-4 gap-y-3">

                <a href="/" class="flex-none rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
                    <logo class="h-8 fill-white text-white" title="CursusGeregeld.nl"></logo>
                    <span class="sr-only">Naar de homepage</span>
                </a>

                <div
                    id="site-search"
                    class="order-last basis-full md:order-none md:basis-auto md:grow md:max-w-2xl md:mx-auto"
                    :class="showSearch ? 'block' : 'hidden md:block'"
                >
                    <AutocompleteBar ref="search" />
                </div>

                <div class="ml-auto md:ml-0 flex flex-none items-center gap-5 lg:gap-8">
                    <button
                        type="button"
                        class="md:hidden inline-flex items-center justify-center rounded-full text-white cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                        aria-controls="site-search"
                        :aria-expanded="showSearch"
                        :aria-label="showSearch ? 'Zoeken sluiten' : 'Zoeken'"
                        @click="toggleSearch"
                    >
                        <icon :name="showSearch ? 'cross' : 'search'" class="size-6"></icon>
                    </button>
                    <cart-basket></cart-basket>
                    <profile-button></profile-button>
                    <!-- A wrapper, because a class on the slideover itself ends up on its panel, not on the menu button -->
                    <div class="flex lg:hidden">
                        <MobileMenu :items="header.navigation" :contact="contact" :reachability="reachability" />
                    </div>
                </div>

            </div>
        </div>

        <DesktopMenu :items="header.navigation" :contact="contact" />

        <ImpersonateBar />

    </header>
</template>
